import PresentationVideo from "../components/PresentationVideo";
import { connectDraw } from "../utils/drawConnection";
import WinnerSequence from "../components/WinnerSequence";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { adminFetch } from "../services/adminFetch";
import { API } from "../constant/apiConstant";
import { Colors } from "../utils/colors";
import { formatAdminTime } from "../utils/adminDateTime";
import { getGrossWinnerAmount, normalizeWinnerPresentation } from "../utils/winnerPresentation";
import { logout as logoutAction } from "../features/auth/authSlice";

const LIVE_STAGES = {
  PRE_SHOW: "pre_show",
  ANNOUNCEMENT: "announcement",
  READY: "ready",
  LOADING: "loading",
  ANIMATION_VIDEO: "animation_video",
  SAMPLING: "sampling",
  FIREWORKS: "fireworks",
  COMPLETED: "completed",
  ERROR: "error"
};

const DEFAULT_DRUMROLL_AUDIO_URL = "";
const DEFAULT_BOOM_AUDIO_URL = "";
const DEFAULT_DRAW_ANIMATION_VIDEO_URL = "";
const DEFAULT_FIREWORKS_VIDEO_URL = "";
const DEFAULT_RNG_SPEED_MS = 90;
const DEFAULT_WINNERS_LIST_SPEED_MPS = 500;

function resolvePresenterStage(state) {
  switch (String(state || "").toUpperCase()) {
    case "PRESENTER_CONNECTED":
      return LIVE_STAGES.PRE_SHOW;
    case "READY_TO_START":
      return LIVE_STAGES.READY;
    case "ANNOUNCEMENT":
      return LIVE_STAGES.ANNOUNCEMENT;
    case "LIVE_DRAW":
    case "LIVE":
      return LIVE_STAGES.ANIMATION_VIDEO;
    case "CELEBRATION":
      return LIVE_STAGES.FIREWORKS;
    case "RESULTS":
    case "COMPLETED":
      return LIVE_STAGES.COMPLETED;
    default:
      return null;
  }
}

const countdownGlowKeyframes = {
  "@keyframes presenterPulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 rgba(251, 192, 45, 0.4)"
    },
    "50%": {
      transform: "scale(1.015)",
      boxShadow: "0 0 28px rgba(251, 192, 45, 0.2)"
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: "0 0 0 rgba(251, 192, 45, 0.4)"
    }
  },
  "@keyframes revealFlash": {
    "0%": {
      opacity: 0.15,
      transform: "scale(0.95)"
    },
    "50%": {
      opacity: 1,
      transform: "scale(1)"
    },
    "100%": {
      opacity: 0.2,
      transform: "scale(1.05)"
    }
  }
};

function formatNaira(amount) {
  return `₦${Number(amount || 0).toLocaleString("en-NG")}`;
}

function formatDrawDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-NG", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

function formatDrawTime(value) {
  return formatAdminTime(value, { includeSeconds: false, fallback: "" });
}

function getPublicDrawDetails(draw) {
  if (!draw) {
    return {
      code: "",
      date: "",
      time: "",
      prize: "₦0",
      winnerCount: 10,
      winnersText: "10 WINNERS"
    };
  }

  const minimumWinners = Number(draw.minimum_winners || 10);
  const displayWinnerCount = Number(draw.display_winner_count || minimumWinners);

  return {
    code: draw.draw_code || "",
    date: formatDrawDate(draw.draw_date),
    time: formatDrawTime(draw.draw_date),
    prize: formatNaira(draw.winner_prize_amount || 0),
    winnerCount: displayWinnerCount,
    winnersText: `${displayWinnerCount} WINNERS`
  };
}

export default function AdminLiveDraw() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminIdentity = useSelector((state) => state.auth?.admin);
  const isPresenter = String(typeof adminIdentity === "string" ? adminIdentity : adminIdentity?.role || "").toLowerCase() === "presenter";

  const [stage, setStage] = useState(LIVE_STAGES.LOADING);
  const [draw, setDraw] = useState(location.state?.draw || null);
  const [mediaSettings, setMediaSettings] = useState({
    drumroll: DEFAULT_DRUMROLL_AUDIO_URL,
    boom: DEFAULT_BOOM_AUDIO_URL,
    animationVideo: DEFAULT_DRAW_ANIMATION_VIDEO_URL,
    preShowVideo: "",
    fireworks: DEFAULT_FIREWORKS_VIDEO_URL,
    resultsAudio: "",
    logoUrl: "",
    rngNote: "",
    rngSpeedMs: DEFAULT_RNG_SPEED_MS,
    winnersListSpeedMps: DEFAULT_WINNERS_LIST_SPEED_MPS,
    announcements: [],
    announcementDurationSeconds: 60
  });
  const [sampledTickets, setSampledTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [progressCount, setProgressCount] = useState(0);
  const [winners, setWinners] = useState([]);
  const [, setPendingWinners] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [resultsAudioBlocked, setResultsAudioBlocked] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);
  const replayInFlightRef = useRef(false);
  const recoverInFlightRef = useRef(false);
  const lastStateTimeRef = useRef(0);
  const [announcementClock, setAnnouncementClock] = useState(Date.now());
  const [connectionNotice,setConnectionNotice]=useState("");
  const [openingReady,setOpeningReady]=useState(false);
  const [openingFailed,setOpeningFailed]=useState(false);
  const resultGeneration=useRef("");
  const resourceLoadInFlight=useRef(false), resourcesReady=useRef(false);
  const [audience, setAudience] = useState({ current_viewers: 0, unique_viewers: 0, peak_concurrent: 0 });

  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    const loadAudience = async () => {
      try {
        const data = await adminFetch(`/api/admin/draws/${id}/audience`, { method: "GET" });
        if (active && data?.status) setAudience(data.audience || {});
      } catch (_error) {
        // Audience analytics must never interrupt presentation.
      }
    };
    loadAudience(); const timer = window.setInterval(loadAudience, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [id]);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const drumrollAudioRef = useRef(null);
  const boomAudioRef = useRef(null);
  const drawAnimationVideoRef = useRef(null);
  const fireworksVideoRef = useRef(null);
  const resultsAudioRef = useRef(null);
  const stageTimeoutRef = useRef(null);
  const samplingTimeoutRef = useRef(null);
  const startSequenceIdRef = useRef(0);
  const celebrationHandledDrawIdRef = useRef(null);
  const serverOffsetRef = useRef(0);

  const publicDraw = useMemo(() => getPublicDrawDetails(draw), [draw]);

  const stopAudio = useCallback((mediaRef) => {
    const element = mediaRef.current;

    if (!element) {
      return;
    }

    try {
      element.pause();
      element.currentTime = 0;
    } catch (error) {
      console.log(error);
    }
  }, []);

  const clearStageTimers = useCallback(() => {
    if (stageTimeoutRef.current) {
      clearTimeout(stageTimeoutRef.current);
      stageTimeoutRef.current = null;
    }

    if (samplingTimeoutRef.current) {
      clearTimeout(samplingTimeoutRef.current);
      samplingTimeoutRef.current = null;
    }

  }, []);

  const stopAllMedia = useCallback(() => {
    stopAudio(drumrollAudioRef);
    stopAudio(boomAudioRef);

    [drawAnimationVideoRef.current, fireworksVideoRef.current].forEach((video) => {
      if (!video) {
        return;
      }

      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        console.log(error);
      }
    });
  }, [stopAudio]);

  const loadFallbackDraw = useCallback(async (signal) => {
    const data = await adminFetch(`/api/admin/draws/${id}`, {
      method: "GET", signal
    });

    return data?.status ? data.draw || null : null;
  }, [id]);

  const loadPresenterResources = useCallback(async () => {
    if(resourceLoadInFlight.current)return;
    resourceLoadInFlight.current=true;
    const controller=new AbortController();
    const timeout=window.setTimeout(()=>controller.abort(),10000);
    setErrorMessage("");

    try {
      const [resolvedDraw, settingsResponse, liveState] = await Promise.all([
        loadFallbackDraw(controller.signal),
        adminFetch("/api/settings", { method: "GET", signal:controller.signal }),
        adminFetch(`/api/admin/draws/${id}/live-display?_=${Date.now()}`, { method: "GET", cache: "no-store", signal:controller.signal })
      ]);

      if (!resolvedDraw || !settingsResponse?.settings) {
        throw new Error("Presentation resources unavailable");
      }
      resourcesReady.current=true;

      const settings = settingsResponse?.settings || {};

      const matchingLiveState = Number(liveState?.draw_id) === Number(resolvedDraw.id) ? liveState : null;
      if (matchingLiveState?.server_time) {
        const stateTime = Date.parse(matchingLiveState.server_time);
        if (stateTime < lastStateTimeRef.current) return;
        lastStateTimeRef.current = stateTime;
        serverOffsetRef.current = stateTime - Date.now();
      }
      const authoritativeState = matchingLiveState?.state || resolvedDraw.presentation_state;
      const resolvedStage = resolvePresenterStage(authoritativeState);
      setDraw({ ...resolvedDraw, ...(matchingLiveState || {}) });
      setMediaSettings({
        drumroll: settings.live_draw_drumroll_audio_url || DEFAULT_DRUMROLL_AUDIO_URL,
        boom: settings.live_draw_winner_boom_audio_url || DEFAULT_BOOM_AUDIO_URL,
        animationVideo:
          resolvedDraw.draw_video_url || settings.live_draw_animation_video_url || DEFAULT_DRAW_ANIMATION_VIDEO_URL,
        preShowVideo: resolvedDraw.pre_draw_video_url || settings.live_draw_pre_draw_video_url || "",
        fireworks: settings.live_draw_fireworks_video_url || DEFAULT_FIREWORKS_VIDEO_URL,
        resultsAudio: settings.live_draw_results_audio_url || "",
        logoUrl: settings.logo_url || "",
        rngNote:
          settings.rng_display_note ||
          "Winners are selected using a certified Random Number Generator (RNG).",
        rngSpeedMs: Math.max(Number(settings.draw_running_speed_ms || DEFAULT_RNG_SPEED_MS), 45),
        winnersListSpeedMps: Math.max(
          Number(settings.winners_list_speed ?? DEFAULT_WINNERS_LIST_SPEED_MPS),
          1
        ),
        announcements: [1, 2, 3, 4, 5].map((page) => settings[`live_draw_announcement_page_${page}`]).filter((copy) => String(copy || "").trim()),
        announcementDurationSeconds: Number(settings.announcement_total_duration_seconds || 60)
      });
      if (resolvedStage === LIVE_STAGES.COMPLETED) {
        setWinners((matchingLiveState?.winners || []).map((winner) =>
          normalizeWinnerPresentation(winner, resolvedDraw.draw_code)
        ));
        setStage(LIVE_STAGES.COMPLETED);
      } else if (resolvedStage === LIVE_STAGES.FIREWORKS) {
        celebrationHandledDrawIdRef.current = Number(resolvedDraw.id);
        setPendingWinners((matchingLiveState?.winners || []).map((winner) =>
          normalizeWinnerPresentation(winner, resolvedDraw.draw_code)
        ));
        setStage(LIVE_STAGES.FIREWORKS);
      } else if (resolvedStage === LIVE_STAGES.ANNOUNCEMENT) {
        setStage(LIVE_STAGES.ANNOUNCEMENT);
      } else if (authoritativeState === "FINALIZATION_ERROR") {
        setStage(LIVE_STAGES.ERROR);
        setErrorMessage("Winner finalization requires administrator review. The draw was not rerun.");
      } else if (resolvedStage) {
        setStage(resolvedStage);
      } else {
        setStage(LIVE_STAGES.ERROR);
        setErrorMessage("The assigned draw has not entered a valid Presenter stage. Please sign in again.");
      }
    } catch (error) {
      resourcesReady.current=false;
      console.warn(JSON.stringify({event:"PRESENTATION_RESOURCES_UNAVAILABLE",drawId:Number(id)}));
      setStage(previous=>previous===LIVE_STAGES.LOADING?LIVE_STAGES.ERROR:previous);
      setErrorMessage("Unable to refresh presentation resources. Retrying; the last known state is preserved.");
    } finally {window.clearTimeout(timeout);resourceLoadInFlight.current=false; }
  }, [id, loadFallbackDraw]);

  useEffect(() => {
    let disposed = false;
    const recover = async () => {
      if (disposed || recoverInFlightRef.current) return;
      recoverInFlightRef.current = true;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      try {
      if(!resourcesReady.current)loadPresenterResources();
      const liveState = await adminFetch(`/api/admin/draws/${id}/live-display?_=${Date.now()}`, { method: "GET", cache: "no-store", signal: controller.signal });
      if(disposed)return;
      if(!liveState || Number(liveState.draw_id)!==Number(id) || !liveState.state)throw new Error('Authoritative draw state unavailable');
      if (liveState.server_time) {
        const stateTime = Date.parse(liveState.server_time);
        if (stateTime < lastStateTimeRef.current) return;
        lastStateTimeRef.current = stateTime;
        serverOffsetRef.current = stateTime - Date.now();
      }
      setDraw((previous) => ({ ...(previous || {}), ...liveState }));
      if (liveState.state === "RESULTS") {
        const generation=`${id}:${liveState.results_started_at}`;
        if(resultGeneration.current!==generation){
          resultGeneration.current=generation;
          startSequenceIdRef.current += 1;
          clearStageTimers();stopAllMedia();
        }
        const next=(liveState.winners || []).map(winner=>normalizeWinnerPresentation(winner,liveState.draw_code));
        setWinners(previous=>JSON.stringify(previous)===JSON.stringify(next)?previous:next);
        setStage(LIVE_STAGES.COMPLETED);
      } else if (liveState.state === "CELEBRATION") {
        if (celebrationHandledDrawIdRef.current === Number(id)) return;
        celebrationHandledDrawIdRef.current = Number(id);
        clearStageTimers();
        stopAllMedia();
        setPendingWinners((liveState.winners || []).map((winner) =>
          normalizeWinnerPresentation(winner, liveState.draw_code)
        ));
        setStage(LIVE_STAGES.FIREWORKS);
      } else if (liveState.state === "FINALIZATION_ERROR") {
        clearStageTimers();
        stopAllMedia();
        setStage(LIVE_STAGES.ERROR);
        setErrorMessage("Winner finalization requires administrator review. The draw was not rerun.");
      } else if (liveState.state === "LIVE_DRAW") {
        celebrationHandledDrawIdRef.current = null;
        setStage(LIVE_STAGES.ANIMATION_VIDEO);
      } else if (liveState.state === "PRESENTER_CONNECTED") {
        setStage(LIVE_STAGES.PRE_SHOW);
      } else if (liveState.state === "ANNOUNCEMENT") {
        setStage(LIVE_STAGES.ANNOUNCEMENT);
      } else if (liveState.state === "READY_TO_START") {
        setStage(LIVE_STAGES.READY);
      }
      } finally { window.clearTimeout(timeout); recoverInFlightRef.current = false; }
    };
    const disconnect=connectDraw({url:`${API.BASE_URL}/api/draws/live-events`,refresh:recover,
      onStatus:status=>{
        if(status.snapshot===false)setConnectionNotice('Draw state refresh failed. Retrying; the last presentation remains visible.');
        else if(status.snapshot===true)setConnectionNotice(status.events?'':'Live events interrupted; checking the authoritative draw state.');
      }});
    return ()=>{disposed=true;disconnect();};
  }, [clearStageTimers, id, loadPresenterResources, stopAllMedia]);

  useEffect(() => {
    if (stage !== LIVE_STAGES.ANNOUNCEMENT) return undefined;
    const tick = () => {
      const serverNow = Date.now() + serverOffsetRef.current;
      setAnnouncementClock(serverNow);
      if (draw?.announcement_ends_at && serverNow >= new Date(draw.announcement_ends_at).getTime()) loadPresenterResources();
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [draw?.announcement_ends_at, loadPresenterResources, stage]);


  useEffect(() => {
    if (stage !== LIVE_STAGES.ANIMATION_VIDEO || !draw?.started_at || !drawAnimationVideoRef.current) return undefined;
    const video = drawAnimationVideoRef.current;
    const sync = () => {
      const elapsed = Math.max(0, (Date.now() + serverOffsetRef.current - new Date(draw.shuffle_started_at || draw.started_at).getTime()) / 1000);
      if (Number.isFinite(video.duration) && video.duration > 0) video.currentTime = elapsed % video.duration;
      video.play().catch(() => {});
    };
    video.addEventListener("loadedmetadata", sync, { once: true });
    if (video.readyState >= 1) sync();
    return () => video.removeEventListener("loadedmetadata", sync);
  }, [draw?.shuffle_started_at, draw?.started_at, stage]);

  useEffect(() => {
    loadPresenterResources();

    return () => {
      clearStageTimers();
      stopAllMedia();
    };
  }, [clearStageTimers, loadPresenterResources, stopAllMedia]);

  useEffect(() => {
    if (stage !== LIVE_STAGES.FIREWORKS) {
      return undefined;
    }

    const video = fireworksVideoRef.current;

    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
        video.muted = false;
        video.defaultMuted = false;
        video.volume = 1;

        const playPromise = video.play();

        if (playPromise?.catch) {
          playPromise.catch((error) => console.log(error));
        }
      } catch (error) {
        console.log(error);
      }
    }
    return undefined;
  }, [stage]);

  useEffect(() => {
    const audio = resultsAudioRef.current;
    if (stage !== LIVE_STAGES.COMPLETED || !audio || !mediaSettings.resultsAudio) {
      if (audio) audio.pause();
      return undefined;
    }
    audio.loop = true;
    audio.play().then(() => setResultsAudioBlocked(false)).catch(() => setResultsAudioBlocked(true));
    return () => audio.pause();
  }, [mediaSettings.resultsAudio, stage]);

  const playDrumroll = () => {
    const element = drumrollAudioRef.current;

    if (!element || !mediaSettings.drumroll) {
      return;
    }

    try {
      element.currentTime = 0;
      element.loop = true;
      const playPromise = element.play();

      if (playPromise?.catch) {
        playPromise.catch((error) => {
          console.log(error);
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const waitForDrawAnimationVideo = useCallback(
    (runSequenceId) =>
      new Promise((resolve, reject) => {
        let attempts = 0;

        const tryResolve = () => {
          if (runSequenceId !== startSequenceIdRef.current) {
            reject(new Error("Live draw presentation was cancelled."));
            return;
          }

          if (drawAnimationVideoRef.current) {
            resolve(drawAnimationVideoRef.current);
            return;
          }

          attempts += 1;

          if (attempts > 20) {
            reject(new Error("Draw animation video could not be prepared."));
            return;
          }

          requestAnimationFrame(tryResolve);
        };

        tryResolve();
      }),
    []
  );

  const playDrawAnimationVideo = useCallback(
    async (runSequenceId) => {
      if (!mediaSettings.animationVideo) {
        throw new Error("No draw animation video configured.");
      }

      setStage(LIVE_STAGES.ANIMATION_VIDEO);
      const video = await waitForDrawAnimationVideo(runSequenceId);

      return new Promise((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
          video.removeEventListener("ended", handleEnded);
          video.removeEventListener("error", handleError);
        };

        const finish = (callback, value) => {
          if (settled) {
            return;
          }

          settled = true;
          cleanup();
          callback(value);
        };

        const handleEnded = () => {
          finish(resolve);
        };

        const handleError = () => {
          finish(reject, new Error("Draw animation video could not be played."));
        };

        video.addEventListener("ended", handleEnded);
        video.addEventListener("error", handleError);

        try {
          video.pause();
          video.currentTime = 0;
          video.muted = false;
          video.defaultMuted = false;
          video.volume = 1;

          const playPromise = video.play();

          if (playPromise?.catch) {
            playPromise.catch(() => {
              handleError();
            });
          }
        } catch (error) {
          handleError();
        }
      });
    },
    [mediaSettings.animationVideo, waitForDrawAnimationVideo]
  );

  const runSamplingPresentation = (previewTickets, runSequenceId, stepMs) =>
    new Promise((resolve) => {
      const tickets = Array.isArray(previewTickets) ? previewTickets : [];

      if (!tickets.length) {
        setActiveTicket(null);
        setProgressCount(0);
        resolve([]);
        return;
      }

      let index = 0;

      const advance = () => {
        if (runSequenceId !== startSequenceIdRef.current) {
          resolve([]);
          return;
        }

        const current = tickets[index];

        setActiveTicket(current);
        setProgressCount(index + 1);

        if (index >= tickets.length - 1) {
          samplingTimeoutRef.current = setTimeout(() => {
            setActiveTicket(null);
            resolve(tickets);
          }, Math.min(stepMs, 90));
          return;
        }

        index += 1;
        samplingTimeoutRef.current = setTimeout(advance, stepMs);
      };

      advance();
    });

  const handleStartDraw = async () => {
    if (draw?.draw_mode === "test" && !window.confirm(
      "TEST MODE hides this draw from public Results only. It still creates real winner, wallet, payout, tax and notification records. Start this draw?"
    )) return;
    clearStageTimers();
    stopAllMedia();

    const currentSequenceId = startSequenceIdRef.current + 1;
    startSequenceIdRef.current = currentSequenceId;

    setIsStarting(true);
    setErrorMessage("");
    setWinners([]);
    setPendingWinners([]);
    setSampledTickets([]);
    setProgressCount(0);
    setActiveTicket(null);

    try {
      const previewTickets = [];
      const previewTicketIds = [];
      const samplingSpeedMs = Math.max(Number(mediaSettings.rngSpeedMs || DEFAULT_RNG_SPEED_MS), 45);

      setSampledTickets(previewTickets);

      const runResponse = await adminFetch(`/api/admin/draws/${id}/run`, {
        method: "POST",
        body: JSON.stringify({
          sampled_ticket_ids: previewTicketIds,
          confirm_test_mode: draw?.draw_mode === "test"
        })
      });
      if (!runResponse?.status) {
        throw new Error(runResponse?.message || "Failed to start the live draw.");
      }
      const authoritativeDraw = runResponse.draw || {};
      setDraw((previous) => ({ ...(previous || {}), ...authoritativeDraw }));
      setStage(LIVE_STAGES.ANIMATION_VIDEO);
      if (runResponse.legacy_client_managed_timeline) {
      const deadlineMs = Math.max(0, new Date(authoritativeDraw.presentation_ends_at || Date.now()).getTime() - Date.now());
      const waitForDeadline = new Promise((resolve) => setTimeout(resolve, deadlineMs + 250));

      if (mediaSettings.animationVideo) {
        try {
          await Promise.race([playDrawAnimationVideo(currentSequenceId), waitForDeadline]);
        } catch (error) {
          console.log("Draw animation video playback failed; backend lifecycle continues:", error);
          if (currentSequenceId !== startSequenceIdRef.current) {
            return;
          }
          setStage(LIVE_STAGES.ANIMATION_VIDEO);
          await waitForDeadline;
        }
      } else {
        setStage(LIVE_STAGES.SAMPLING);
        playDrumroll();
        await Promise.race([
          runSamplingPresentation(previewTickets, currentSequenceId, samplingSpeedMs),
          waitForDeadline
        ]);
      }

      if (currentSequenceId !== startSequenceIdRef.current) {
        return;
      }

      stopAllMedia();
      setStage(LIVE_STAGES.ANIMATION_VIDEO);
      stopAudio(drumrollAudioRef);
      setStage(LIVE_STAGES.ANIMATION_VIDEO);

      for (let attempt = 0; attempt < 120; attempt += 1) {
        const liveState = await adminFetch(`/api/admin/draws/${id}/live-display`, { method: "GET" });
        if (Number(liveState?.draw_id) === Number(id) && liveState.state === "CELEBRATION") {
          celebrationHandledDrawIdRef.current = Number(id);
          setPendingWinners((liveState.winners || []).map((winner) =>
            normalizeWinnerPresentation(winner, liveState.draw_code)
          ));
          setStage(LIVE_STAGES.FIREWORKS);
          break;
        }
        if (Number(liveState?.draw_id) === Number(id) && liveState.state === "RESULTS") {
          setWinners((liveState.winners || []).map((winner) =>
            normalizeWinnerPresentation(winner, liveState.draw_code)
          ));
          setStage(LIVE_STAGES.COMPLETED);
          break;
        }
        if (Number(liveState?.draw_id) === Number(id) && liveState.state === "FINALIZATION_ERROR") {
          throw new Error("Winner finalization requires administrator review. The draw was not rerun.");
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      }
    } catch (error) {
      console.log(error);
      stopAllMedia();
      if (/press next|ready stage/i.test(error?.message || "")) {
        // Recover a stale Presenter screen from the persisted stage instead of
        // replacing a valid pre-show with a client-only error screen.
        await loadPresenterResources();
      } else {
        setStage(LIVE_STAGES.ERROR);
        setErrorMessage(error?.message || "Something went wrong while running the live draw.");
      }
    } finally {
      setIsStarting(false);
    }
  };

  const handleNext = async () => {
    setIsStarting(true);
    setErrorMessage("");
    try {
      const response = await adminFetch(`/api/admin/draws/${id}/ready`, { method: "POST" });
      if (!response?.status) throw new Error(response?.message || "Unable to advance the presentation.");
      setDraw((previous) => ({ ...(previous || {}), ...(response.draw || {}) }));
      setStage(response.state === "READY_TO_START" ? LIVE_STAGES.READY : LIVE_STAGES.ANNOUNCEMENT);
    } catch (error) {
      setErrorMessage(error.message || "Unable to advance the presentation.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleReplayWinnerReveal = async () => {
    if (!winners.length || replayInFlightRef.current) return;
    replayInFlightRef.current = true;
    setIsReplaying(true);
    setErrorMessage("");
    try {
      const response = await adminFetch(`/api/admin/draws/${id}/replay`, {
        method: "POST", body: JSON.stringify({ results_started_at: draw.results_started_at })
      });
      if (!response?.status) throw new Error(response?.message || "Unable to replay results");
      setDraw(previous => ({ ...previous, results_started_at: response.resultsStartedAt }));
      if (resultsAudioRef.current) {
        resultsAudioRef.current.currentTime = 0;
        resultsAudioRef.current.play().then(() => setResultsAudioBlocked(false)).catch(() => setResultsAudioBlocked(true));
      }
    } catch (error) { setErrorMessage(error.message); }
    finally { replayInFlightRef.current = false; setIsReplaying(false); }
  };

  const handlePresenterExit = async () => {
    setIsExiting(true);
    try {
      const response = await fetch(`${API.BASE_URL}/api/admin/auth/presenter/exit`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.status) throw new Error(payload?.message || "Unable to exit presentation");
      dispatch(logoutAction());
      navigate("/presenter/login", { replace: true });
    } catch (error) {
      setErrorMessage(error.message || "Unable to exit presentation");
      setExitDialogOpen(false);
    } finally {
      setIsExiting(false);
    }
  };

  const renderReadyStage = () => (
    <Card
      elevation={14}
      sx={{
        width: "100%",
        maxWidth: 1080,
        borderRadius: 5,
        px: { xs: 3, sm: 4, lg: 6 },
        py: { xs: 4, sm: 5, lg: 6 },
        color: "#fff",
        background:
          "radial-gradient(circle at center, rgba(251,192,45,0.1), rgba(10,61,98,0.96) 38%, rgba(5,24,42,1) 100%)",
        border: "1px solid rgba(251,192,45,0.24)",
        position: "relative",
        overflow: "hidden",
        animation: "presenterPulse 4.5s ease-in-out infinite",
        ...countdownGlowKeyframes
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ position: "fixed", top: 16, left: 16, zIndex: 20, px: 1.5, py: 1, borderRadius: 2, bgcolor: "rgba(2,7,13,.82)", border: "1px solid rgba(255,255,255,.15)" }}>
        <Typography variant="body2" fontWeight={800}>● {Number(audience.current_viewers || 0)} Live</Typography>
        {!isPresenter ? <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)" }}>Unique {Number(audience.unique_viewers || 0)} · Peak {Number(audience.peak_concurrent || 0)}</Typography> : null}
      </Stack>
      <Box
        sx={{
          position: "absolute",
          inset: "auto -12% -30% auto",
          width: { xs: 220, md: 360 },
          height: { xs: 220, md: 360 },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(126,217,87,0.2), transparent 70%)",
          pointerEvents: "none"
        }}
      />

      <Stack spacing={4} sx={{ position: "relative", zIndex: 1, alignItems: "center" }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Typography variant="h3" sx={{ fontWeight: 900 }}>
            DRAW {publicDraw.code}
          </Typography>
          <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.2rem" } }}>{publicDraw.date}</Typography>
          <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.2rem" } }}>{publicDraw.time}</Typography>
          <Typography sx={{ pt: 1, fontWeight: 800 }}>WINNERS: {publicDraw.winnerCount}</Typography>
          <Typography sx={{ fontWeight: 900, color: Colors.accent }}>
            PRIZE PER WINNER: {publicDraw.prize}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "relative",
            width: { xs: 280, sm: 360, md: 460 },
            height: { xs: 240, sm: 320, md: 380 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "visible"
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: { xs: 240, sm: 320, md: 410 },
              height: { xs: 150, sm: 210, md: 260 },
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              opacity: 0.82,
              filter: "blur(0.2px)",
              background: `
                radial-gradient(circle at center, rgba(255,80,30,0.12) 0%, rgba(255,80,30,0.08) 24%, transparent 56%),
                repeating-radial-gradient(circle at center,
                  transparent 0 42px,
                  rgba(255,74,34,0.38) 42px 44px,
                  transparent 44px 59px)
              `
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: 260, sm: 340, md: 430 },
              height: 18,
              bottom: { xs: 28, sm: 38, md: 46 },
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,201,100,0.85) 0%, rgba(255,141,0,0.5) 28%, rgba(255,120,0,0.16) 58%, rgba(255,120,0,0) 100%)",
              filter: "blur(7px)",
              pointerEvents: "none"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: 214, sm: 274, md: 330 },
              height: { xs: 214, sm: 274, md: 330 },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,195,71,0.12) 0%, rgba(255,135,0,0.1) 32%, rgba(255,100,20,0) 72%)",
              boxShadow: "0 0 40px rgba(255,120,0,0.22)",
              pointerEvents: "none"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: 224, sm: 284, md: 340 },
              height: { xs: 224, sm: 284, md: 340 },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,227,146,0.22) 0%, rgba(255,227,146,0.08) 18%, rgba(255,227,146,0) 50%)",
              pointerEvents: "none"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: 210, sm: 270, md: 326 },
              height: { xs: 210, sm: 270, md: 326 },
              borderRadius: "50%",
              border: "2px solid rgba(255,214,110,0.45)",
              boxShadow:
                "0 0 0 6px rgba(255,130,32,0.18), 0 0 30px rgba(255,110,20,0.35), inset 0 0 16px rgba(255,231,152,0.22)",
              pointerEvents: "none",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: { xs: 10, md: 12 },
                borderRadius: "50%",
                border: "8px solid rgba(255,120,10,0.18)"
              },
              "&::after": {
                content: '""',
                position: "absolute",
                inset: { xs: 5, md: 6 },
                borderRadius: "50%",
                background:
                  "repeating-conic-gradient(from 0deg, rgba(255,219,123,0.95) 0deg 9deg, rgba(255,186,73,0.24) 9deg 18deg)",
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 18px), #000 calc(100% - 18px))",
                mask:
                  "radial-gradient(farthest-side, transparent calc(100% - 18px), #000 calc(100% - 18px))",
                opacity: 0.95
              }
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: { xs: 26, sm: 30, md: 34 },
              left: { xs: 64, sm: 82, md: 112 },
              width: { xs: 22, sm: 28, md: 34 },
              height: { xs: 22, sm: 28, md: 34 },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,249,214,1) 0%, rgba(255,202,102,0.95) 28%, rgba(255,150,31,0.35) 55%, rgba(255,150,31,0) 100%)",
              filter: "blur(0.3px)",
              pointerEvents: "none"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              pointerEvents: "none"
            }}
          />
          <Button
            variant="contained"
            onClick={handleStartDraw}
            disabled={isStarting}
            sx={{
              width: { xs: 176, sm: 228, md: 276 },
              height: { xs: 176, sm: 228, md: 276 },
              borderRadius: "50%",
              px: 0,
              minWidth: 0,
              background: `
                radial-gradient(circle at 30% 24%, rgba(255,232,205,0.92) 0%, rgba(255,232,205,0.18) 13%, rgba(255,232,205,0) 24%),
                radial-gradient(circle at 50% 18%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 32%),
                radial-gradient(circle at 50% 32%, #ff2a1b 0%, #df0d10 45%, #8a0101 78%, #570000 100%)
              `,
              color: "#ffe89d",
              boxShadow:
                "0 0 0 2px rgba(255,170,70,0.18), 0 16px 32px rgba(0,0,0,0.42), 0 0 24px rgba(255,88,18,0.32), inset 0 6px 18px rgba(255,255,255,0.18), inset 0 -16px 24px rgba(0,0,0,0.38)",
              textAlign: "center",
              border: "2px solid rgba(255,208,118,0.18)",
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                background: `
                  radial-gradient(circle at 30% 24%, rgba(255,232,205,0.92) 0%, rgba(255,232,205,0.18) 13%, rgba(255,232,205,0) 24%),
                  radial-gradient(circle at 50% 18%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 32%),
                  radial-gradient(circle at 50% 32%, #ff3824 0%, #ea1212 45%, #980303 78%, #620000 100%)
                `
              },
              "&::before": {
                content: '""',
                position: "absolute",
                inset: { xs: 8, md: 10 },
                borderRadius: "50%",
                border: "1px solid rgba(255,224,141,0.28)",
                boxShadow: "inset 0 0 24px rgba(255,255,255,0.1)"
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: { xs: 20, md: 24 },
                left: "50%",
                transform: "translateX(-50%)",
                width: "64%",
                height: "20%",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0) 78%)"
              }
            }}
          >
            <Stack spacing={0.5} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: "2.6rem", sm: "3.4rem", md: "4.2rem" },
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: "0.03em",
                  color: "#ffe48d",
                  textShadow:
                    "0 3px 0 rgba(123,49,0,0.82), 0 0 12px rgba(255,228,141,0.18), 0 10px 18px rgba(0,0,0,0.34)"
                }}
              >
                START
              </Typography>
            </Stack>
          </Button>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ width: "100%", display: "none" }}
          justifyContent="center"
          alignItems="stretch"
        >
          {[
            { label: "Draw", value: publicDraw.code || "Pending" },
            { label: "Date", value: publicDraw.date || "Not set" },
            { label: "Time", value: publicDraw.time || "Not set" },
            { label: "Prize Per Winner", value: publicDraw.prize }
          ].map((item) => (
            <Card
              key={item.label}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: 3,
                p: 2.5,
                bgcolor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff"
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 0.5 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", md: "1.35rem" } }}>
                {item.value}
              </Typography>
            </Card>
          ))}
        </Stack>

        <Card
          sx={{
            display: "none",
            width: "100%",
            maxWidth: 760,
            borderRadius: 3,
            p: { xs: 2.5, md: 3 },
            bgcolor: "rgba(255,255,255,0.06)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: Colors.accent }}>
              {publicDraw.winnersText}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.74)" }}>
              {mediaSettings.rngNote}
            </Typography>
          </Stack>
        </Card>

      </Stack>
    </Card>
  );

  const renderPreShowStage = () => (
    <Card sx={{ width: "100%", maxWidth: 1080, p: { xs: 1, sm: 2.5, md: 4 }, bgcolor: "#071f36", color: "#fff" }}>
      <Stack spacing={{ xs: 1.5, sm: 3 }} alignItems="center">
        <Typography variant="h3" fontWeight={900} color={Colors.accent} sx={{ display: { xs: "none", sm: "block" } }}>LIVE DRAW PRESENTATION</Typography>
        {mediaSettings.preShowVideo ? (
          <Box sx={{ width: "100%", maxWidth: { xs: 480, sm: 960 }, height: { xs: "72svh", sm: "auto" }, aspectRatio: { xs: "9/16", sm: "16/9" }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#000", borderRadius: 3, overflow: "hidden" }}>
            <PresentationVideo src={mediaSettings.preShowVideo} loop startedAt={draw?.presenter_connected_at} serverOffset={serverOffsetRef.current}
              onReady={ready=>{setOpeningReady(ready);if(ready)setOpeningFailed(false);}} onError={()=>setOpeningFailed(true)} />
          </Box>
        ) : <Alert severity="info">Pre-show video is unavailable. You can still continue safely.</Alert>}
        <Button variant="contained" onClick={handleNext} disabled={isStarting || !openingReady}
          sx={{ alignSelf: { xs: "stretch", sm: "flex-end" }, minWidth: 160, bgcolor: Colors.accent, color: "#071f36", fontWeight: 900 }}>
          RUN ANNOUNCEMENT
        </Button>
        {(openingFailed || !mediaSettings.preShowVideo) && <Button onClick={()=>{if(window.confirm('Opening media is unavailable. Continue to announcements without it?'))handleNext();}}>Continue without opening video</Button>}
      </Stack>
    </Card>
  );

  const renderAnnouncementStage = () => {
    const startedAt = new Date(draw?.announcement_started_at || announcementClock).getTime();
    const totalSeconds = Number(draw?.announcement_duration_seconds || mediaSettings.announcementDurationSeconds || 60);
    const activeAnnouncements = (draw?.announcements || mediaSettings.announcements).filter((copy) => String(copy || "").trim());
    const pageCount = Math.max(activeAnnouncements.length, 1);
    const pageIndex = Math.min(pageCount - 1, Math.floor(Math.max(0, announcementClock - startedAt) / ((totalSeconds * 1000) / pageCount)));
    const copy = activeAnnouncements[pageIndex] || "Live draw announcement";
    return (
      <Card sx={{ width: "100%", maxWidth: 540, height: { xs: "78svh", sm: 680 }, bgcolor: "#071f36", color: "#fff", p: { xs: 3, sm: 5 }, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%", pt: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" fontWeight={900} color={Colors.accent}>ANNOUNCEMENT</Typography>
          <Box sx={{ pt: { xs: 1.5, sm: 2 } }}>
            <Typography sx={{ whiteSpace: "pre-line", textAlign: "left", overflowWrap: "break-word", fontSize: { xs: "1.25rem", sm: "1.65rem" }, lineHeight: 1.45, fontWeight: 700 }}>{copy}</Typography>
          </Box>
          <Typography color="rgba(255,255,255,.6)" textAlign="center">{pageIndex + 1} / {activeAnnouncements.length}</Typography>
        </Stack>
      </Card>
    );
  };

  const renderSamplingStage = () => (
    <Card
      elevation={12}
      sx={{
        width: "100%",
        maxWidth: 980,
        borderRadius: 5,
        px: { xs: 3, md: 6 },
        py: { xs: 4, md: 5.5 },
        color: "#fff",
        background:
          "linear-gradient(180deg, rgba(9,34,56,0.98) 0%, rgba(6,22,39,1) 100%)",
        border: "1px solid rgba(251,192,45,0.22)"
      }}
    >
      <Stack spacing={3} alignItems="center" textAlign="center">
        <Chip
          label="RNG Running"
          sx={{
            bgcolor: "rgba(126,217,87,0.16)",
            color: Colors.primary,
            fontWeight: 700
          }}
        />
        <Typography variant="h3" sx={{ fontWeight: 800, color: Colors.accent }}>
          {publicDraw.code || "Live Draw"}
        </Typography>

        <Card
          sx={{
            width: "100%",
            maxWidth: 760,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#fff"
          }}
        >
          <Stack spacing={2.5} alignItems="center">
            <Typography
              sx={{
                fontSize: { xs: "2.4rem", md: "3rem" },
                fontWeight: 900,
                color: Colors.accent,
                minHeight: "1.2em"
              }}
            >
              {activeTicket?.name || "..."}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", md: "1.2rem" } }}>
              {activeTicket?.ticket_code || ""}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
              {progressCount > 0 && sampledTickets.length
                ? `${progressCount} of ${sampledTickets.length} tickets processed`
                : "Preparing ticket sequence..."}
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 10,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.08)",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  width: sampledTickets.length
                    ? `${Math.min((progressCount / sampledTickets.length) * 100, 100)}%`
                    : "0%",
                  height: "100%",
                  borderRadius: 999,
                  bgcolor: Colors.accent,
                  transition: "width 90ms linear"
                }}
              />
            </Box>
          </Stack>
        </Card>
      </Stack>
    </Card>
  );

  const renderAnimationVideoStage = () => (
    <Card
      elevation={12}
      sx={{
        width: "100%",
        maxWidth: 1080,
        borderRadius: 5,
        p: { xs: 0.5, sm: 2, md: 3.5 },
        color: "#fff",
        background:
          "linear-gradient(180deg, rgba(9,34,56,0.98) 0%, rgba(6,22,39,1) 100%)",
        border: "1px solid rgba(251,192,45,0.22)"
      }}
    >
      <Stack alignItems="center" textAlign="center">
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: 480, sm: 960 },
            height: { xs: "88svh", sm: "auto" },
            aspectRatio: { xs: "9/16", sm: "16/9" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#000"
          }}
        >
          <PresentationVideo
            ref={drawAnimationVideoRef}
            src={mediaSettings.animationVideo}
            playsInline
            autoPlay
            muted
            loop
            controls
            preload="auto"
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
      </Stack>
    </Card>
  );

  const renderFireworksStage = () => (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#02070d",
        px: 2
      }}
    >
      {mediaSettings.fireworks ? (
        <Box
          component="video"
          ref={fireworksVideoRef}
          src={mediaSettings.fireworks}
          playsInline
          preload="auto"
          sx={{
            width: "100%",
            maxWidth: 1280,
            maxHeight: "100vh",
            objectFit: "contain",
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "#000"
          }}
        />
      ) : (
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Typography variant="h2" sx={{ color: Colors.accent, fontWeight: 900 }}>
            Winners Unveiling
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
            Preparing the final reveal.
          </Typography>
        </Stack>
      )}
    </Box>
  );

  const renderCompletedStage = () => {
    const durationSeconds = Number(draw?.results_animation_duration_seconds || Math.max(18, Number(winners.length || 1) * 5));
    return (
    <Card
      elevation={12}
      sx={{
        width: "100%",
        maxWidth: 1120,
        minHeight: { xs: "auto", md: "80vh" },
        borderRadius: 5,
        p: { xs: 3, md: 5 },
        color: "#fff",
        background:
          "linear-gradient(180deg, rgba(9,34,56,0.98) 0%, rgba(6,22,39,1) 100%)",
        border: "1px solid rgba(251,192,45,0.22)"
      }}
    >
      <Stack spacing={3} sx={{ height: "100%" }}>
        <Stack spacing={1.2} alignItems="center" textAlign="center">
          <Typography variant="h3" sx={{ fontWeight: 900, color: Colors.accent }}>
            WINNERS
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

        {resultsAudioBlocked ? (
          <Button variant="outlined" onClick={() => resultsAudioRef.current?.play().then(() => setResultsAudioBlocked(false)).catch(() => {})}
            sx={{ alignSelf: "center", color: "#fff", borderColor: "rgba(255,255,255,.5)" }}>
            🔊 Play Results Audio
          </Button>
        ) : null}

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <WinnerSequence count={winners.length} startedAt={draw?.results_started_at}
          durationSeconds={durationSeconds} serverOffset={serverOffsetRef.current} sx={{ pr: 1 }}>
          <Stack spacing={{ xs: 1.25, sm: 2 }}>
            {winners.map((winner, index) => (
              <Card
                key={winner.ticket_number}
                data-winner-index={index}
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  p: { xs: 1.25, sm: 2, md: 2.5 },
                  bgcolor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: "#fff"
                }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    left: { xs: 12, sm: 20, md: 24 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
                    lineHeight: 1.1,
                    fontWeight: 900,
                    color: Colors.accent
                  }}
                >
                  {index + 1}.
                </Typography>

                <Stack
                  spacing={0.25}
                  alignItems="center"
                  textAlign="center"
                  sx={{
                    width: "100%",
                    maxWidth: "calc(100% - 96px)",
                    minWidth: 0,
                    mx: "auto"
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.6rem" },
                      lineHeight: 1.1,
                      fontWeight: 900,
                      color: Colors.accent,
                      overflowWrap: "anywhere"
                    }}
                  >
                    {winner.name || "Winner"}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "0.95rem", md: "1.08rem" },
                      overflowWrap: "anywhere"
                    }}
                  >
                    {winner.ticket_number || ""}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: Colors.accent,
                      fontSize: { xs: "1.05rem", md: "1.2rem" }
                    }}
                  >
                    {formatNaira(getGrossWinnerAmount(winner))}
                  </Typography>
                </Stack>
              </Card>
            ))}
          </Stack>
        </WinnerSequence>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="contained"
            onClick={handleReplayWinnerReveal}
            disabled={isReplaying}
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              py: 1.3,
              borderRadius: 999,
              fontWeight: 800,
              bgcolor: Colors.accent,
              color: "#081b2c",
              "&:hover": {
                bgcolor: "#ffd455"
              }
            }}
          >
            REPLAY
          </Button>
        </Stack>
      </Stack>
    </Card>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#04111d",
        background:
          "radial-gradient(circle at top, rgba(251,192,45,0.08), transparent 24%), linear-gradient(180deg, #0a3d62 0%, #04111d 100%)",
        color: "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: {
          xs: stage === LIVE_STAGES.FIREWORKS ? "stretch" : "center",
          md: stage === LIVE_STAGES.FIREWORKS ? "stretch" : "center"
        },
        px: { xs: 2, sm: 3, md: 4 },
        py: stage === LIVE_STAGES.FIREWORKS ? 0 : { xs: 3, md: 5 },
        overflow: "hidden"
      }}
    >
      {draw?.draw_mode === "test" && <Chip label="TEST MODE" color="warning"
        sx={{ position: "fixed", top: 16, left: 16, zIndex: 20, fontWeight: 800 }} />}
      {isPresenter ? (
        <Button variant="outlined" color="error" onClick={() => setExitDialogOpen(true)} sx={{ position: "fixed", top: 16, right: 16, zIndex: 20, bgcolor: "rgba(2,7,13,.8)" }}>
          EXIT PRESENTATION
        </Button>
      ) : null}
      {mediaSettings.drumroll ? (
        <audio ref={drumrollAudioRef} src={mediaSettings.drumroll} preload="auto" />
      ) : null}
      {mediaSettings.boom ? (
        <audio ref={boomAudioRef} src={mediaSettings.boom} preload="auto" />
      ) : null}
      {mediaSettings.resultsAudio ? (
        <audio ref={resultsAudioRef} src={mediaSettings.resultsAudio} preload="auto" loop />
      ) : null}
      <Dialog open={exitDialogOpen} onClose={() => !isExiting && setExitDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Exit presentation?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to exit the presentation?</Typography>
          <Typography mt={1} color="text.secondary">This presenter account will expire and cannot be reused.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitDialogOpen(false)} disabled={isExiting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handlePresenterExit} disabled={isExiting}>{isExiting ? "Exiting…" : "Exit"}</Button>
        </DialogActions>
      </Dialog>

      {stage === LIVE_STAGES.LOADING ? (
        <Stack spacing={2.5} alignItems="center">
          <CircularProgress sx={{ color: Colors.accent }} size={68} thickness={4} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Preparing Live Draw Screen
          </Typography>
        </Stack>
      ) : null}

      {connectionNotice && <Alert severity="warning">{connectionNotice}</Alert>}
      {stage === LIVE_STAGES.PRE_SHOW ? renderPreShowStage() : null}
      {stage === LIVE_STAGES.ANNOUNCEMENT ? renderAnnouncementStage() : null}
      {stage === LIVE_STAGES.READY ? renderReadyStage() : null}
      {stage === LIVE_STAGES.ANIMATION_VIDEO ? renderAnimationVideoStage() : null}
      {stage === LIVE_STAGES.SAMPLING ? renderSamplingStage() : null}
      {stage === LIVE_STAGES.FIREWORKS ? renderFireworksStage() : null}
      {stage === LIVE_STAGES.COMPLETED ? renderCompletedStage() : null}

      {stage === LIVE_STAGES.ERROR ? (
        <Card
          elevation={12}
          sx={{
            width: "100%",
            maxWidth: 720,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            bgcolor: "rgba(9,34,56,0.95)",
            color: "#fff"
          }}
        >
          <Stack spacing={2.5}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Live Draw Unavailable
            </Typography>
            <Alert severity="error">{errorMessage || "Unable to continue the live draw."}</Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                onClick={loadPresenterResources}
                sx={{
                  bgcolor: Colors.accent,
                  color: "#081b2c",
                  fontWeight: 800,
                  "&:hover": {
                    bgcolor: "#ffd455"
                  }
                }}
              >
                Try Again
              </Button>
            </Stack>
          </Stack>
        </Card>
      ) : null}
    </Box>
  );
}
